import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  UserCheck, 
  UserX, 
  X, 
  Shield, 
  Mail, 
  MoreHorizontal, 
  Download, 
  Plus, 
  CheckCircle, 
  XCircle, 
  Trash2, 
  Search, 
  Edit,
  Save,
  Crown,
  Settings,
  AlertCircle,
  Lock
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { 
  getAllAdmins,
  getAllAdminPermissions,
  getAdminPermissions,
  setAdminPermissions,
  deleteAdminPermissions,
  createUserProfile,
  updateUserProfile,
  deleteUserProfile
} from '../../firebase/collections';
import { createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase/config';
import toast from 'react-hot-toast';

// All available admin sections
const ALL_SECTIONS = [
  { id: 'dashboard', name: 'Dashboard', icon: '📊' },
  { id: 'exam-management', name: 'Exam Management', icon: '🛡️' },
  { id: 'test-management', name: 'Test Management', icon: '📝' },
  { id: 'live-class-management', name: 'Live Class Management', icon: '📹' },
  { id: 'course-management', name: 'Course Management', icon: '🎓' },
  { id: 'resource-management', name: 'Resource Management', icon: '📁' },
  { id: 'user-management', name: 'User Management', icon: '👥' },
  { id: 'tuition-teachers', name: 'Tuition Teachers', icon: '👨‍🏫' },
  { id: 'coaching-management', name: 'Coaching Management', icon: '🏢' },
  { id: 'coaching-admin-permissions', name: 'Coaching Admin Permissions', icon: '🛡️' },
  { id: 'all-applications', name: 'All Applications', icon: '📄' },
  { id: 'refunds', name: 'Refunds', icon: '💳' },
  { id: 'settlements', name: 'Settlements', icon: '💰' },
  { id: 'all-institutions', name: 'All Institutions', icon: '🏛️' },
  { id: 'teacher-requests', name: 'Teacher Requests', icon: '📧' },
  { id: 'vacancy-management', name: 'Vacancy Management', icon: '➕' },
  { id: 'batch-management', name: 'Batch Management', icon: '👥' },
  { id: 'help-requests', name: 'Help Requests', icon: '💬' },
  { id: 'support-tickets', name: 'Support Tickets', icon: '🎫' },
  { id: 'doubts-management', name: 'Doubts Management', icon: '❓' },
  { id: 'ai-knowledge', name: 'AI Knowledge Management', icon: '🧠' },
  { id: 'team-management', name: 'Team Management', icon: '👔' },
  { id: 'system-settings', name: 'System Settings', icon: '⚙️' },
  { id: 'poll-management', name: 'Poll Management', icon: '📊' },
  { id: 'activity-registration', name: 'Activity Registration', icon: '🎯' },
  { id: 'leaderboard-management', name: 'Leaderboard Management', icon: '🏆' },
  { id: 'proctoring', name: 'Proctoring', icon: '🛡️' },
  { id: 'analytics', name: 'Analytics', icon: '📈' },
  { id: 'data-migration', name: 'Data Migration', icon: '💾' },
  { id: 'performance-monitor', name: 'Performance Monitor', icon: '⚡' },
  { id: 'firebase-index-helper', name: 'Firebase Index Helper', icon: '🔍' }
];

const AdminManagement = () => {
  const { currentUser, logAdminAction } = useRealtime();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDeleteAdminConfirm, setShowDeleteAdminConfirm] = useState(null);
  const [showResetPasswordConfirm, setShowResetPasswordConfirm] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    password: '',
    displayName: '',
    role: 'admin'
  });

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      const [adminsResult, allPermissionsResult] = await Promise.all([
        getAllAdmins(),
        getAllAdminPermissions()
      ]);

      if (adminsResult.success) {
        const adminsList = adminsResult.data || [];
        
        // Load permissions for each admin
        const adminsWithPermissions = await Promise.all(
          adminsList.map(async (admin) => {
            const permResult = await getAdminPermissions(admin.email);
            return {
              ...admin,
              permissions: permResult.success && permResult.data ? permResult.data : null
            };
          })
        );

        setAdmins(adminsWithPermissions);
      }
    } catch (error) {
      toast.error('Failed to load admins: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPermissions = (admin) => {
    setEditingAdmin(admin);
    const currentPerms = admin.permissions?.permissions || [];
    setPermissions({
      allowedSections: [...currentPerms],
      isActive: admin.permissions?.isActive !== false,
      notes: admin.permissions?.notes || ''
    });
  };

  const handleToggleSection = (sectionId) => {
    setPermissions(prev => {
      const sections = prev.allowedSections || [];
      if (sections.includes(sectionId)) {
        return {
          ...prev,
          allowedSections: sections.filter(id => id !== sectionId)
        };
      } else {
        return {
          ...prev,
          allowedSections: [...sections, sectionId]
        };
      }
    });
  };

  const handleSavePermissions = async () => {
    if (!editingAdmin) return;

    try {
      setSaving(true);
      const result = await setAdminPermissions(editingAdmin.email, {
        ...permissions,
        createdBy: currentUser?.email || 'founder'
      });

      if (result.success) {
        toast.success('Permissions updated successfully!');
        await loadAdmins();
        setEditingAdmin(null);
        setPermissions({});
      } else {
        toast.error('Failed to update permissions: ' + result.error);
      }
    } catch (error) {
      toast.error('Error updating permissions: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePermissions = async (adminEmail) => {
    try {
      const result = await deleteAdminPermissions(adminEmail);
      if (result.success) {
        toast.success('Permissions removed successfully!');
        await loadAdmins();
        setShowDeleteConfirm(null);
      } else {
        toast.error('Failed to remove permissions: ' + result.error);
      }
    } catch (error) {
      toast.error('Error removing permissions: ' + error.message);
    }
  };

  const handleCreateAdmin = async () => {
    if (!newAdmin.email || !newAdmin.password || !newAdmin.displayName) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newAdmin.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Validate password length
    if (newAdmin.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    try {
      setCreating(true);
      
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        newAdmin.email,
        newAdmin.password
      );
      const user = userCredential.user;

      // Create user profile with admin role
      const userProfileData = {
        uid: user.uid,
        email: user.email,
        displayName: newAdmin.displayName,
        name: newAdmin.displayName,
        role: newAdmin.role || 'admin',
        isAdmin: true,
        isActive: true,
        profileCompleted: true,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const profileResult = await createUserProfile(userProfileData);

      if (profileResult.success) {
        toast.success('Admin created successfully!');
        
        // Log admin action
        if (logAdminAction) {
          await logAdminAction('create_admin', {
            adminEmail: newAdmin.email,
            adminName: newAdmin.displayName,
            role: newAdmin.role
          });
        }

        // Reset form and reload admins
        setNewAdmin({
          email: '',
          password: '',
          displayName: '',
          role: 'admin'
        });
        setShowCreateForm(false);
        await loadAdmins();
      } else {
        // Note: Firebase Auth user cannot be deleted from client-side
        // The user will need to be deleted manually from Firebase Console
        toast.error('Failed to create admin profile: ' + profileResult.error);
        toast.error('Note: Please delete the Firebase Auth user manually from Firebase Console if needed.');
      }
    } catch (error) {
      let errorMessage = 'Failed to create admin: ';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage += 'Email is already in use';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage += 'Invalid email address';
      } else if (error.code === 'auth/weak-password') {
        errorMessage += 'Password is too weak';
      } else {
        errorMessage += error.message;
      }
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteAdmin = async (admin) => {
    if (!admin.id) {
      toast.error('Cannot delete admin: Missing user ID');
      return;
    }

    try {
      setDeleting(true);

      // Delete permissions first
      if (admin.permissions) {
        await deleteAdminPermissions(admin.email);
      }

      // Delete user profile
      const deleteProfileResult = await deleteUserProfile(admin.id);
      
      if (!deleteProfileResult.success) {
        toast.error('Failed to delete admin profile: ' + deleteProfileResult.error);
        return;
      }

      // Note: We can't delete Firebase Auth users from client-side
      // The user will need to be deleted from Firebase Console or via Admin SDK
      // Instead, we'll mark them as inactive
      await updateUserProfile(admin.id, {
        isActive: false,
        deletedAt: new Date(),
        deletedBy: currentUser?.email || 'founder'
      });

      // Log admin action
      if (logAdminAction) {
        await logAdminAction('delete_admin', {
          adminEmail: admin.email,
          adminName: admin.displayName || admin.name
        });
      }

      toast.success('Admin deleted successfully! Note: Firebase Auth user must be deleted manually from Firebase Console.');
      await loadAdmins();
      setShowDeleteAdminConfirm(null);
    } catch (error) {
      toast.error('Error deleting admin: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleResetPassword = async (admin) => {
    if (!admin.email) {
      toast.error('Cannot reset password: Missing email address');
      return;
    }

    try {
      setResettingPassword(true);
      
      // Send password reset email
      await sendPasswordResetEmail(auth, admin.email);
      
      // Log admin action
      if (logAdminAction) {
        await logAdminAction('reset_admin_password', {
          adminEmail: admin.email,
          adminName: admin.displayName || admin.name
        });
      }

      toast.success(`Password reset email sent to ${admin.email}`);
      setShowResetPasswordConfirm(null);
    } catch (error) {
      let errorMessage = 'Failed to send password reset email: ';
      if (error.code === 'auth/user-not-found') {
        errorMessage += 'User not found';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage += 'Invalid email address';
      } else {
        errorMessage += error.message;
      }
      toast.error(errorMessage);
    } finally {
      setResettingPassword(false);
    }
  };

  const filteredAdmins = admins.filter(admin => {
    const searchLower = searchTerm.toLowerCase();
    return (
      admin.email?.toLowerCase().includes(searchLower) ||
      admin.displayName?.toLowerCase().includes(searchLower) ||
      admin.name?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Crown className="h-8 w-8 text-yellow-500" />
            Admin Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage admin permissions and role-based access control
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus size={20} />
          Create Admin
        </button>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search admins by email or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      {/* Admins List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Admin
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Permissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredAdmins.map((admin) => {
                const allowedSections = admin.permissions?.permissions || [];
                const isActive = admin.permissions?.isActive !== false;
                
                return (
                  <tr key={admin.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold">
                            {admin.displayName?.[0] || admin.name?.[0] || admin.email?.[0] || 'A'}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {admin.displayName || admin.name || 'Unknown'}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {admin.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        admin.role === 'founder' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                        admin.role === 'superadmin' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                        'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                      }`}>
                        {admin.role || 'admin'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {allowedSections.length > 0 ? (
                          <span className="text-green-600 dark:text-green-400">
                            {allowedSections.length} section{allowedSections.length !== 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span className="text-gray-500 dark:text-gray-400">No permissions set</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isActive ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEditPermissions(admin)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-4"
                      >
                        <Edit size={16} className="inline mr-1" />
                        Edit
                      </button>
                      {admin.permissions && (
                        <button
                          onClick={() => setShowDeleteConfirm(admin.email)}
                          className="text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300 mr-4"
                        >
                          <XCircle size={16} className="inline mr-1" />
                          Remove Permissions
                        </button>
                      )}
                      <button
                        onClick={() => setShowResetPasswordConfirm(admin)}
                        className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300 mr-4"
                      >
                        <Lock size={16} className="inline mr-1" />
                        Reset Password
                      </button>
                      {admin.role !== 'founder' && (
                        <button
                          onClick={() => setShowDeleteAdminConfirm(admin)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <Trash2 size={16} className="inline mr-1" />
                          Delete Admin
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Permissions Modal */}
      {editingAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Edit Permissions: {editingAdmin.email}
                </h2>
                <button
                  onClick={() => {
                    setEditingAdmin(null);
                    setPermissions({});
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Active Status
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={permissions.isActive}
                    onChange={(e) => setPermissions(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Admin is active and can access allowed sections
                  </span>
                </label>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Allowed Sections
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-96 overflow-y-auto p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                  {ALL_SECTIONS.map((section) => {
                    const isSelected = permissions.allowedSections?.includes(section.id);
                    return (
                      <label
                        key={section.id}
                        className={`flex items-center p-3 rounded-lg cursor-pointer border-2 transition-colors ${
                          isSelected
                            ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-500'
                            : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSection(section.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          {section.icon} {section.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes
                </label>
                <textarea
                  value={permissions.notes || ''}
                  onChange={(e) => setPermissions(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  rows="3"
                  placeholder="Add notes about this admin's permissions..."
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setEditingAdmin(null);
                    setPermissions({});
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePermissions}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Save Permissions
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Create Admin Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Create New Admin
                </h2>
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewAdmin({
                      email: '',
                      password: '',
                      displayName: '',
                      role: 'admin'
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Display Name *
                  </label>
                  <input
                    type="text"
                    value={newAdmin.displayName}
                    onChange={(e) => setNewAdmin(prev => ({ ...prev, displayName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={newAdmin.email}
                    onChange={(e) => setNewAdmin(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="admin@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Password *
                  </label>
                  <input
                    type="password"
                    value={newAdmin.password}
                    onChange={(e) => setNewAdmin(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Minimum 6 characters"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Role
                  </label>
                  <select
                    value={newAdmin.role}
                    onChange={(e) => setNewAdmin(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="admin">Admin</option>
                    <option value="superadmin">Super Admin</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewAdmin({
                      email: '',
                      password: '',
                      displayName: '',
                      role: 'admin'
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateAdmin}
                  disabled={creating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {creating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus size={16} />
                      Create Admin
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Admin Confirmation Modal */}
      {showDeleteAdminConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="h-6 w-6 text-red-500" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Delete Admin?
              </h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete <strong>{showDeleteAdminConfirm.displayName || showDeleteAdminConfirm.name || showDeleteAdminConfirm.email}</strong>? 
              This will:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Remove their admin profile</li>
                <li>Delete their permissions</li>
                <li>Mark them as inactive</li>
              </ul>
              <span className="text-sm text-orange-600 dark:text-orange-400 mt-2 block">
                Note: Firebase Auth user must be deleted manually from Firebase Console.
              </span>
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteAdminConfirm(null)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteAdmin(showDeleteAdminConfirm)}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete Admin
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Reset Password Confirmation Modal */}
      {showResetPasswordConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <Lock className="h-6 w-6 text-purple-500" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Reset Password?
              </h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              A password reset email will be sent to <strong>{showResetPasswordConfirm.email}</strong>. 
              The admin will receive an email with instructions to reset their password.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowResetPasswordConfirm(null)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleResetPassword(showResetPasswordConfirm)}
                disabled={resettingPassword}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {resettingPassword ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Lock size={16} />
                    Send Reset Email
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Permissions Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="h-6 w-6 text-red-500" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Remove Permissions?
              </h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to remove permissions for <strong>{showDeleteConfirm}</strong>? 
              This will revoke all their section access.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeletePermissions(showDeleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Remove Permissions
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;

